import React, { useState, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Form, Button, Spinner, Container, Row, Col } from 'react-bootstrap';
import DateFilterInput from '../Utils/DateFilterInput';
import { convertDateToISO, getCurrentDate, getCurrentDateMinusDays } from '../Utils/utils';
import { estoqueReportPdf, estoqueReportCsv } from '../../services/Reports/reportsService';
import ProductInput from '../Utils/ProductInput';
import { usePermissions } from '../../hooks/usePermissions';
import { usePermissionsContext } from '../../context/PermissionsContext';

const Estoque = () => {
  const { checkPermission, ToastComponent, hasPermission } = usePermissions();
  const { permissions } = usePermissionsContext();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCsv, setIsLoadingCsv] = useState(false);
  const { handleSubmit, formState: {}, control, watch, setValue, getValues, trigger } = useForm({});
  const watchedFields = watch(['licensePlate', 'person', 'driver', 'product', 'initialDate', 'finalDate']);
  const [licensePlate, person, driver, product] = watchedFields;
  const isAnyOtherFieldFilled = licensePlate || person || driver || product;

  // Recalcula permissões sempre que o objeto permissions mudar
  const canView = useMemo(() => hasPermission('reportsView'), [hasPermission, permissions]);

  const formatPayload = (data) => {
    const getSafeDate = (dateStr, fallback) => {
      if (!dateStr || dateStr.trim() === '') {
        return fallback;
      }
      const isValid = /^\d{2}\/\d{2}\/\d{4}$/.test(dateStr);
      return isValid ? convertDateToISO(dateStr) : fallback;
    };

    const defaultInitialDate = getCurrentDateMinusDays(30);
    const defaultFinalDate = getCurrentDate();

    return {
      initialDate: getSafeDate(data?.initialDate, defaultInitialDate),
      finalDate: getSafeDate(data?.finalDate, defaultFinalDate),
      product: data.product?.id || null
    };
  };

  const onSubmit = async (data) => {
    if (!checkPermission('reportsView')) {
      return;
    }
    setIsLoading(true);
    try {
      const formattedPayload = formatPayload(data);
      await estoqueReportPdf(formattedPayload);
    } 
    catch (error) {
      console.log(error);
    } 
    finally {
      setIsLoading(false);
    }
  };

  const onExportCsv = async () => {
    if (!checkPermission('reportsView')) {
      return;
    }
    
    const isValid = await trigger(['initialDate', 'finalDate']);
    if (!isValid) {
      return;
    }
    
    setIsLoadingCsv(true);
    try {
      const formData = getValues();
      const formattedPayload = formatPayload(formData);
      await estoqueReportCsv(formattedPayload);
    } 
    catch (error) {
      console.log(error);
    } 
    finally {
      setIsLoadingCsv(false);
    }
  };

  if (!canView) {
    return (
      <>
        <ToastComponent />
        <Container>
          <h3 className="mb-4 mt-2 text-center text-md-start">Relatório de Estoque</h3>
        </Container>
      </>
    );
  }

  return (
    <Container>
      <ToastComponent />
      <h3 className="mb-4 mt-2 text-center text-md-start">Relatório de Estoque</h3>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Form.Group className="mb-3">
          <Row>
            <Col md={3}>
              <Controller
                name="initialDate"
                control={control}
                rules={{
                  required: 'A data inicial é obrigatória.',
                  validate: (value) => {
                    const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
                    if (value && !dateRegex.test(value)) return 'Data inválida';
                    return true;
                  }
                }}
                render={({ field, fieldState }) => (
                  <DateFilterInput
                    label="Data Inicial"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.error}
                    required={true}
                  />
                )}
              />
            </Col>
            <Col md={3}>
              <Controller
                name="finalDate"
                control={control}
                rules={{
                  required: 'A data final é obrigatória.',
                  validate: (value) => {
                    const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
                    if (value && !dateRegex.test(value)) return 'Data inválida';
                    return true;
                  }
                }}
                render={({ field, fieldState }) => (
                  <DateFilterInput
                    label="Data Final"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.error}
                    required={true}
                  />
                )}
              />
            </Col>
            <Col md={6}>
              <Controller
                name="product"
                control={control}
                render={({ field, fieldState }) => (
                  <ProductInput
                    value={field.value}
                    createdNew={false}
                    onChange={(selected) => {
                      setValue('product', selected);
                    }}
                    onCreated={(created) => {
                      setValue('product', { id: created.id, name: created.name });
                    }}
                    label="Produto / Cultura"
                    isInvalid={!!fieldState.error}
                  />
                )}
              />
            </Col>
          </Row>
        </Form.Group>
        <div className="d-flex gap-2">
          <Button
            variant="primary"
            className="button-custom"
            type="submit"
            disabled={isLoading || isLoadingCsv}
          >
            {isLoading ? (
              <>
                Aguarde <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              </>
            ) : (
              'Imprimir PDF'
            )}
          </Button>
          <Button
            variant="success"
            className="button-custom"
            type="button"
            onClick={onExportCsv}
            disabled={isLoading || isLoadingCsv}
          >
            {isLoadingCsv ? (
              <>
                Aguarde <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              </>
            ) : (
              'Exportar CSV'
            )}
          </Button>
        </div>
      </Form>
    </Container>
  );
};

export default Estoque;